<script setup lang="ts">
// One insight run's requests on the approvals page, folded into one card (F5, ORP-22267).
// Registered into module 31's inbox through `registerApprovalInboxGrouping`; the inbox renders it
// for a `group` entry and re-reads itself on `decided`.
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

import { useApprovalRunDecision } from '../composables/useApprovalRunDecision'
import { useReasonText } from '../composables/useReasonText'
import type { ApprovalRunGroup } from '../service/approvalRunGroups'
import { humanizeKey } from '../service/labels'

import ActionOutcomeSummary from './ActionOutcomeSummary.vue'

import {
  CHANGE_DETAIL_ROUTE,
  useApprovalEligibility,
  type ApprovalInboxRow,
} from '@/features/platform-actions'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'

const props = defineProps<{ group: ApprovalRunGroup }>()
const emit = defineEmits<{ decided: [] }>()

const { t } = useI18n()
const format = useFormat()
const { requestError } = useReasonText()
const eligibility = useApprovalEligibility()
const decision = useApprovalRunDecision(() => props.group)
const { asking, busy, errorCode, selected, outcome, decidingCount } = decision

const open = ref(false)

const first = computed(() => props.group.rows[0])
const platform = computed(
  () => humanizeKey(first.value?.request.platform) || t('insightActions.history.thePlatform'),
)
const proposer = computed(
  () => first.value?.proposer?.displayName || t('insightActions.history.requesterRemoved'),
)
const askedAgo = computed(() =>
  first.value ? format.formatRelative(first.value.request.createdAt, { numeric: 'always' }) : '',
)
const summary = computed(() =>
  t('insightActions.approvals.summary', {
    count: props.group.pendingTotal,
    platform: platform.value,
    who: proposer.value,
    ago: askedAgo.value,
  }),
)

/**
 * The same gate each row's own decision panel applies: role, then maker-checker. The run card
 * offers its verbs only where at least one of its requests could be decided by this viewer.
 */
const canDecide = computed(() => props.group.rows.some((row) => eligibility.canDecide(row)))
const required = computed(() => first.value?.requiredApproval)

const confirmLine = computed(() =>
  t(
    asking.value === 'approve'
      ? 'insightActions.approvals.confirmApprove'
      : 'insightActions.approvals.confirmReject',
    { count: decidingCount.value, platform: platform.value },
  ),
)
const confirmLabel = computed(() => {
  if (busy.value) return t('insightActions.approvals.sending')
  return asking.value === 'approve'
    ? t('insightActions.approvals.yesApprove')
    : t('insightActions.approvals.yesReject')
})
const decisionError = computed(() => requestError(errorCode.value))

function rowLabel(row: ApprovalInboxRow): string {
  return props.group.summaries[row.request.id] || humanizeKey(row.request.actionType)
}

async function confirm(): Promise<void> {
  if (await decision.decide()) emit('decided')
}
</script>

<template>
  <li
    class="rounded-lg border border-subtle bg-surface"
    data-testid="approval-run-card"
    :data-run="props.group.runId"
  >
    <div class="flex flex-wrap items-start justify-between gap-md px-md py-sm">
      <button
        type="button"
        class="flex min-w-0 flex-1 flex-col items-start gap-2xs border-0 bg-transparent p-0 text-start"
        :aria-expanded="open"
        data-testid="run-card-toggle"
        @click="open = !open"
      >
        <span class="truncate font-semibold text-fg">{{ props.group.origin.insightTitle }}</span>
        <span class="text-sm text-muted">{{ summary }}</span>
        <span class="text-2xs text-muted">{{ props.group.origin.configLabel }}</span>
      </button>

      <div v-if="canDecide" class="flex shrink-0 items-center gap-xs">
        <Button
          size="sm"
          variant="outline"
          data-testid="run-reject"
          @click="decision.ask('reject')"
        >
          {{ t('insightActions.approvals.reject') }}
        </Button>
        <Button size="sm" data-testid="run-approve" @click="decision.ask('approve')">
          {{ t('insightActions.approvals.approve', { count: decidingCount }) }}
        </Button>
      </div>
      <!-- The authority a decision needs, as a role — never the members who hold it. -->
      <p v-else-if="required" class="m-0 text-xs text-muted" data-testid="run-needs-role">
        {{
          t(
            required.requiresSecondPerson
              ? 'insightActions.approvals.needsRoleSecond'
              : 'insightActions.approvals.needsRole',
            { role: required.requiredRole },
          )
        }}
      </p>
    </div>

    <div
      v-if="asking"
      class="border-t border-subtle bg-danger-soft px-md py-sm"
      data-testid="run-confirm"
    >
      <p class="m-0 text-xs font-semibold text-danger">{{ confirmLine }}</p>
      <p class="mt-2xs mb-0 text-xs text-muted">{{ t('insightActions.approvals.ownDecision') }}</p>
      <div class="mt-xs flex items-center gap-xs">
        <Button variant="soft" size="xs" data-testid="run-confirm-keep" @click="decision.keep()">
          {{ t('insightActions.approvals.back') }}
        </Button>
        <Button
          variant="primary"
          size="xs"
          :disabled="busy"
          data-testid="run-confirm-yes"
          @click="confirm"
        >
          {{ confirmLabel }}
        </Button>
      </div>
    </div>

    <p
      v-if="errorCode"
      class="m-0 border-t border-subtle bg-danger-soft px-md py-xs text-xs text-danger"
      role="alert"
      data-testid="run-error"
    >
      {{ decisionError.message }} <span class="font-mono">{{ decisionError.code }}</span>
    </p>

    <div v-if="outcome" class="border-t border-subtle px-md py-sm" data-testid="run-outcome">
      <ActionOutcomeSummary :submitted="outcome.submitted" :buckets="outcome.buckets" />
    </div>

    <div v-if="open" class="border-t border-subtle">
      <div
        v-for="row in props.group.rows"
        :key="row.request.id"
        class="flex items-center gap-sm border-b border-subtle px-md py-xs text-sm last:border-0"
      >
        <Checkbox
          v-if="canDecide"
          :model-value="selected.has(row.request.id)"
          :aria-label="rowLabel(row)"
          :data-testid="`row-pick-${row.request.id}`"
          @update:model-value="decision.toggle(row.request.id)"
        />
        <span class="min-w-0 flex-1 truncate">{{ rowLabel(row) }}</span>
        <RouterLink
          :to="{ name: CHANGE_DETAIL_ROUTE, params: { actionId: row.request.id } }"
          class="shrink-0 text-2xs text-muted hover:underline"
        >
          {{ t('insightActions.approvals.details') }}
        </RouterLink>
      </div>

      <p
        v-if="selected.size"
        class="m-0 px-md py-xs text-2xs text-muted"
        data-testid="run-selection"
      >
        {{
          t('insightActions.approvals.selection', {
            count: selected.size,
            total: props.group.rows.length,
          })
        }}
      </p>
    </div>
  </li>
</template>
